/**
 * SPEC-AgentQattScope — pure helpers shared by the admin Users page (client)
 * and the user update path (server). Dependency-free.
 *
 * `users.allowed_qatts`: NULL = may check every task type; otherwise a
 * non-empty list of TASK_CATEGORIES values. An EMPTY array is never stored —
 * NULL is the one spelling of "unrestricted".
 */

export const QATT_OPTIONS: { value: number; label: string; title: string }[] = [
  { value: 1, label: "CU", title: "QATT 1 — CookUnity dropoff" },
  { value: 2, label: "B2B", title: "QATT 2 — B2B dropoff" },
  { value: 3, label: "Reg", title: "QATT 3 — Regular dropoff" },
  { value: 4, label: "Pickup", title: "QATT 4 — Pickup" },
];

/** Tick / untick one task type. Unticking the last box returns null, never []. */
export function toggleQatt(current: number[] | null | undefined, value: number, on: boolean): number[] | null {
  const set = new Set(current ?? []);
  if (on) set.add(value);
  else set.delete(value);
  const next = Array.from(set).sort((a, b) => a - b);
  return next.length === 0 ? null : next;
}

/** Same scope? Order-insensitive; null / undefined / [] all mean unrestricted. */
export function sameQatts(a: number[] | null | undefined, b: number[] | null | undefined): boolean {
  const norm = (v: number[] | null | undefined) =>
    v && v.length > 0 ? [...v].sort((x, y) => x - y).join(",") : "";
  return norm(a) === norm(b);
}

/**
 * What a PATCH means for the `allowed_qatts` column:
 *   role changes to anything but 'agent' → clear, whatever else was sent. The
 *       scope only applies to Agents; a leftover value would silently come
 *       back if the user were made an Agent again, and would restrict an
 *       Admin previewing as Agent. (The Users page clears it too; this makes
 *       a direct API call behave the same.)
 *   undefined → leave the column alone
 *   null      → clear the restriction (store NULL)
 *   [..]      → store it sorted; an empty array also clears (never store {})
 */
export function allowedQattsColumnUpdate(
  patchValue: number[] | null | undefined,
  patchRole?: string,
): { write: false } | { write: true; value: number[] | null } {
  if (patchRole !== undefined && patchRole !== "agent") return { write: true, value: null };
  if (patchValue === undefined) return { write: false };
  if (patchValue === null || patchValue.length === 0) return { write: true, value: null };
  return { write: true, value: [...patchValue].sort((a, b) => a - b) };
}
